import { Fragment, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Activity, Fuel, MapPinned, Plane, X } from "lucide-react";
import { buildOpsDayPreview, currentGroundOpsForFlight, currentGroundOpsNodesForFlight, flightActionWindowOpenMinute, groundServiceWindowOpenMinute, sortFlightEvents } from "../../../domain/simulation";
import type { FlightLedgerPreviewOverrides, FlightLedgerResult } from "../../../domain/simulation";
import { dayMinuteToLabel } from "../../../domain/rules/schedule";
import type { ActiveOpsDay, FlightInstance, FlightOccurrenceEvent, FlightReleaseDraft, GlobalEvent, GroundOpsFlowStatus, GroundOpsNodeKind, GroundOpsNodeState, GroundOpsNodeStatus, RedispatchAction, SaveGame, ScheduleSource, WorldData } from "../../../domain/types";
import { formatMoney, formatRotationStatus, formatSeverity } from "../../format";
import { AirportSearch } from "../../components/AirportSearch";
import { PanelHeader, StatusLamp } from "../../components/Panel";
import { LedgerMeter, LedgerRows, SearchableChoice, formatKg } from "./PlayerShared";
import { WorldMap } from "./PlayerMap";
import { OPS_DAY_END_MINUTE } from "./playerTypes";
import { rotationPathLabel, rotationServiceDaysLabel } from "./playerData";


type RedispatchSubmitHandler = (action: RedispatchAction) => Promise<void>;
type FetchFlightReleaseDraftHandler = (flightInstanceId: string) => Promise<FlightReleaseDraft>;
type FetchFlightLedgerPreviewHandler = (flightInstanceId: string, overrides?: FlightLedgerPreviewOverrides) => Promise<FlightLedgerResult | undefined>;
type AircraftReleaseCandidate = NonNullable<FlightInstance["releaseDraft"]>["aircraftCandidates"][number];

export function TimelineView({
  save,
  world,
  schedule,
  activeEvents,
  onRedispatchFlight,
  onFetchFlightReleaseDraft,
  onFetchFlightLedgerPreview
}: {
  save: SaveGame;
  world: WorldData;
  schedule: ScheduleSource;
  activeEvents: GlobalEvent[];
  onRedispatchFlight: RedispatchSubmitHandler;
  onFetchFlightReleaseDraft: FetchFlightReleaseDraftHandler;
  onFetchFlightLedgerPreview: FetchFlightLedgerPreviewHandler;
}) {
  const recentOperations = save.operationsLog.slice(0, 12);
  const opsTimeline = useMemo(() => buildOpsDayPreview(save, schedule, activeEvents), [save, schedule, activeEvents]);
  const [selectedFlightId, setSelectedFlightId] = useState("");
  const isPreview = !save.activeOpsDay;
  const selectedFlight = selectedTimelineFlight(selectedFlightId, opsTimeline);
  const activeOpsEvents = activeOpsEventsForSave(save);
  const selectedEvents = activeOpsEvents.filter((event) => event.flightInstanceId === selectedFlight?.id);
  const selectedOccurrenceEvents = selectedOccurrenceEventsForFlight(selectedFlight, schedule);

  useEffect(() => {
    if (selectedFlightId && !opsTimeline.flights.some((flight) => flight.id === selectedFlightId)) {
      setSelectedFlightId("");
    }
  }, [opsTimeline.flights, selectedFlightId]);

  return (
    <section className="player-view timeline-layout">
      <section className="panel timeline-map-panel">
        <PanelHeader icon={<MapPinned size={15} />} title="GM 计划网络" code="MAP" />
        <WorldMap world={world} flightPlans={schedule.flightPlans.filter((plan) => plan.active)} />
      </section>
      <section className="panel timeline-program-panel">
        <PanelHeader icon={<Plane size={15} />} title="玩家航班串计划" code="ROT" />
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>航班串</th>
                <th>服务日</th>
                <th>航段链</th>
                <th>派机规则</th>
              </tr>
            </thead>
            <tbody>
              {save.rotations.map((rotation) => (
                <tr key={rotation.id}>
                  <td>{rotation.name}</td>
                  <td>{rotationServiceDaysLabel(rotation, schedule)}</td>
                  <td>{rotationPathLabel(rotation, schedule)}</td>
                  <td><StatusLamp active={rotation.legs.length > 0} />始发站自动择机</td>
                </tr>
              ))}
              {save.rotations.length === 0 && (
                <tr>
                  <td colSpan={4}>暂无玩家航班串，请先在“编组”页保存。</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel timeline-execution-panel">
        <PanelHeader icon={<Activity size={15} />} title={timelinePanelTitle(isPreview)} code="EXEC" />
        <div className="timeline-execution-grid">
          <OpsDayAircraftTimeline save={save} ops={opsTimeline} preview={isPreview} selectedFlightId={selectedFlightId} onSelectFlight={setSelectedFlightId} />
          <div className="timeline-status-dock">
            {renderTimelineStatusDock(save.activeOpsDay, activeOpsEvents, recentOperations)}
          </div>
        </div>
      </section>
      {selectedFlight && (
        <FlightDetailDialog
          save={save}
          world={world}
          flight={selectedFlight}
          events={selectedEvents}
          occurrenceEvents={selectedOccurrenceEvents}
          canRedispatch={save.activeOpsDay?.status === "open"}
          onRedispatchFlight={onRedispatchFlight}
          onFetchFlightReleaseDraft={onFetchFlightReleaseDraft}
          onFetchFlightLedgerPreview={onFetchFlightLedgerPreview}
          onClose={() => setSelectedFlightId("")}
        />
      )}
    </section>
  );
}

function selectedTimelineFlight(selectedFlightId: string, opsTimeline: ActiveOpsDay): FlightInstance | undefined {
  if (!selectedFlightId) {
    return undefined;
  }
  return opsTimeline.flights.find((flight) => flight.id === selectedFlightId);
}

function activeOpsEventsForSave(save: SaveGame): NonNullable<SaveGame["activeOpsDay"]>["events"] {
  if (save.activeOpsDay) {
    return sortFlightEvents(save.activeOpsDay.events);
  }
  return [];
}

function timelinePanelTitle(isPreview: boolean): string {
  if (isPreview) {
    return "预生成运营日时间轴 / 按飞机";
  }
  return "实时航班追踪 / 按飞机";
}

function renderTimelineStatusDock(
  activeOpsDay: SaveGame["activeOpsDay"],
  activeOpsEvents: NonNullable<SaveGame["activeOpsDay"]>["events"],
  recentOperations: SaveGame["operationsLog"]
): ReactNode {
  if (activeOpsDay) {
    return <FlightEventStream events={activeOpsEvents.slice(-24)} compact/>;
  }
  return <OperationTimeline operations={recentOperations}/>;
}

function selectedOccurrenceEventsForFlight(selectedFlight: FlightInstance | undefined, schedule: ScheduleSource): FlightOccurrenceEvent[] {
  if (!selectedFlight?.occurrenceId) {
    return [];
  }
  return schedule.occurrenceEvents.filter((event) => event.occurrenceId === selectedFlight.occurrenceId && event.status === "active" && selectedFlight.appliedOccurrenceEventIds.includes(event.id));
}

function formatOptionalMinute(label: string, minute: number | undefined): string {
  if (minute === undefined) {
    return "";
  }
  return ` / ${label} ${dayMinuteToLabel(minute)}`;
}

function formatActualMinute(minute: number | undefined): string {
  if (minute === undefined) {
    return "--";
  }
  return dayMinuteToLabel(minute);
}

function aircraftFuelOnBoardLabel(aircraft: SaveGame["fleet"][number] | undefined, includePrefix = true): string {
  if (!aircraft) {
    return "--";
  }
  const value = formatKg(aircraft.fuelOnBoardKg);
  if (includePrefix) {
    return `FOB ${value}`;
  }
  return value;
}

function aircraftReleaseReadyMinute(candidate: AircraftReleaseCandidate | undefined, fallbackMinute: number): number {
  if (candidate && Number.isFinite(candidate.readyAtMinute)) {
    return candidate.readyAtMinute;
  }
  return fallbackMinute;
}

function fuelOrderStatus(
  fuel: FlightInstance["fuelPlan"] | undefined,
  releaseFuelKg: number | undefined,
  lowerThanMinimum: boolean | undefined,
  aboveMaximum: boolean | undefined
): string {
  if (!fuel) {
    return "等待派机";
  }
  if (lowerThanMinimum && releaseFuelKg !== undefined) {
    return `低于最低 ${formatKg(fuel.minimumReleaseFuelKg - releaseFuelKg)}`;
  }
  if (aboveMaximum && releaseFuelKg !== undefined) {
    return `超过上限 ${formatKg(releaseFuelKg - fuel.maximumReleaseFuelKg)}`;
  }
  return `落地 ${formatKg(fuel.plannedLandingFuelKg)}`;
}

function readinessTransferTitleParts(readiness: NonNullable<FlightInstance["readiness"]>): string[] {
  if (readiness.passengerTransferMinutes > 0) {
    return [`转运 ${readiness.passengerTransferMinutes}m`];
  }
  return [];
}

function flightRotationLabel(save: SaveGame, flight: FlightInstance): string {
  if (flight.rotationId) {
    return `${flight.sourceTitle} / ${rotationBindingLabel(flight.rotationBinding)}`;
  }
  if (flight.recoveryForFlightInstanceId) {
    return `恢复/调机 / 承接 ${recoverySourceLabel(save, flight.recoveryForFlightInstanceId)}`;
  }
  return "恢复/调机 / 独立";
}

function rotationBindingLabel(binding: FlightInstance["rotationBinding"]): string {
  if (binding === "auto_bound") {
    return "自动规则";
  }
  return "本班解绑";
}

function releaseActionReadout(
  flight: FlightInstance,
  beforeActionWindow: boolean,
  actionWindowOpenMinute: number,
  aircraftReadyForService: boolean,
  aircraftReadyMinute: number
): string {
  if (flight.status === "airborne") {
    return "飞行中";
  }
  if (flight.status === "diverted") {
    return flight.reason ?? "备降中";
  }
  if (flight.status === "landed" || flight.status === "cancelled" || flight.status === "failed") {
    return flightReasonLabel(flight);
  }
  if (beforeActionWindow) {
    return `保障窗口 ${dayMinuteToLabel(actionWindowOpenMinute)} 开启`;
  }
  if (!aircraftReadyForService) {
    return `飞机预计 ${dayMinuteToLabel(aircraftReadyMinute)} 到站`;
  }
  return flight.reason ?? "等待处置";
}

function flightReasonLabel(flight: FlightInstance): string {
  if (flight.status === "airborne" && (!flight.reason || isReadyForDepartureReason(flight.reason))) {
    return "飞行中";
  }
  if (flight.status === "diverted" && !flight.reason) {
    return "备降中";
  }
  if (flight.status === "landed" && !flight.reason) {
    return "已落地";
  }
  return flight.reason ?? "正常";
}

function isReadyForDepartureReason(reason: string): boolean {
  return reason === "地面保障完成，等待起飞";
}

function timelineFlightClassName(status: FlightInstance["status"], selected: boolean, selectable: boolean): string {
  const classes = ["timeline-flight", status];
  if (selected) {
    classes.push("active");
  }
  if (selectable) {
    classes.push("selectable");
  }
  return classes.join(" ");
}

function OpsDayAircraftTimeline({
  save,
  ops,
  preview,
  selectedFlightId,
  onSelectFlight
}: {
  save: SaveGame;
  ops: ActiveOpsDay;
  preview: boolean;
  selectedFlightId?: string;
  onSelectFlight?: (flightId: string) => void;
}) {
  const orderedFlights = [...ops.flights].sort((a, b) => a.estimatedDepartureMinute - b.estimatedDepartureMinute || a.flightNo.localeCompare(b.flightNo));
  const timelineFlights = orderedFlights.filter((flight) => flight.plannedDepartureMinute >= 0 && flight.plannedDepartureMinute < OPS_DAY_END_MINUTE);
  const minMinute = Math.min(0, ...timelineFlights.map((flight) => flight.estimatedDepartureMinute));
  const maxMinute = Math.max(OPS_DAY_END_MINUTE, ...timelineFlights.map((flight) => flight.estimatedArrivalMinute));
  const span = Math.max(60, maxMinute - minMinute);
  const groups = aircraftTimelineGroups(save, timelineFlights);
  const ticks = timelineTicks(minMinute, maxMinute);

  return (
    <div className="aircraft-timeline">
      <div className="ops-clock-strip">
        <strong>D{ops.day} / {opsClockLabel(preview, ops.currentMinute)}</strong>
        <span>{timelineFlights.length} 班 / {groups.length} 架飞机 / {opsClockStatusLabel(preview)}</span>
      </div>
      <div className="timeline-scale" aria-hidden="true">
        {ticks.map((minute) => (
          <span key={minute} style={{ left: `${((minute - minMinute) / span) * 100}%` }}>
            {dayMinuteToLabel(minute)}
          </span>
        ))}
      </div>
      <div className="aircraft-lanes">
        {groups.map((group) => (
          <div key={group.id} className="aircraft-lane">
            <div className="aircraft-lane-label">
              <strong>{group.label}</strong>
              <span>{group.subLabel}</span>
            </div>
            <div className="aircraft-lane-track">
              {group.flights.map((flight, index) => {
                const previousFlight = previousFlightForTimelineGroup(group.id, group.flights, index);
                const turnWindow = turnWindowForFlight(flight, previousFlight, minMinute, maxMinute, span);
                const left = ((flight.estimatedDepartureMinute - minMinute) / span) * 100;
                const width = ((flight.estimatedArrivalMinute - flight.estimatedDepartureMinute) / span) * 100;
                const flightLabel = timelineFlightLabel(flight);
                const isSelected = flight.id === selectedFlightId;
                const className = timelineFlightClassName(flight.status, isSelected, Boolean(onSelectFlight));
                const content = (
                  <>
                    <strong>{flight.flightNo}</strong>
                    <span>{flight.origin}-{flight.destination}</span>
                    <em>{dayMinuteToLabel(flight.estimatedDepartureMinute)} - {dayMinuteToLabel(flight.estimatedArrivalMinute)}</em>
                  </>
                );
                const style = { left: `${left}%`, width: `${width}%` };
                return (
                  <Fragment key={flight.id}>
                    {turnWindow && (
                      <div
                        className={timelineTurnWindowClassName(turnWindow.shortfallMinutes)}
                        style={turnWindow.style}
                        title={turnWindow.title}
                        aria-hidden="true"
                      >
                        {turnWindow.minimumMarkerLeft !== undefined && <i style={{ left: `${turnWindow.minimumMarkerLeft}%` }} />}
                        {turnWindow.showLabel && <span>{turnWindow.label}</span>}
                      </div>
                    )}
                    <div className="timeline-flight-shell" style={style}>
                      {renderTimelineFlightCard(onSelectFlight, flight, className, flightLabel, isSelected, content)}
                    </div>
                  </Fragment>
                );
              })}
            </div>
          </div>
        ))}
        {groups.length === 0 && <div className="empty-state">当前没有可生成的运营日航班。</div>}
      </div>
    </div>
  );
}

function opsClockLabel(preview: boolean, currentMinute: number): string {
  if (preview) {
    return "预生成";
  }
  return dayMinuteToLabel(currentMinute);
}

function opsClockStatusLabel(preview: boolean): string {
  if (preview) {
    return "未启动运行日";
  }
  return "实时状态";
}

function previousFlightForTimelineGroup(groupId: string, flights: FlightInstance[], index: number): FlightInstance | undefined {
  if (groupId === "unassigned") {
    return undefined;
  }
  return previousTurnSource(flights, index);
}

function timelineFlightLabel(flight: FlightInstance): string {
  return `${flight.flightNo} ${flight.origin}-${flight.destination} ${dayMinuteToLabel(flight.estimatedDepartureMinute)} - ${dayMinuteToLabel(flight.estimatedArrivalMinute)}${flightReadinessSuffix(flight)}`;
}

function flightReadinessSuffix(flight: FlightInstance): string {
  if (flight.readiness) {
    return ` / 最早准备 ${dayMinuteToLabel(flight.readiness.readyAtMinute)}`;
  }
  return "";
}

function timelineTurnWindowClassName(shortfallMinutes: number): string {
  if (shortfallMinutes > 0) {
    return "timeline-turn-window short";
  }
  return "timeline-turn-window";
}

function renderTimelineFlightCard(
  onSelectFlight: ((flightId: string) => void) | undefined,
  flight: FlightInstance,
  className: string,
  flightLabel: string,
  isSelected: boolean,
  content: ReactNode
): ReactNode {
  if (onSelectFlight) {
    return (
      <button type="button" className={className} title={flightLabel} aria-pressed={isSelected} onClick={() => onSelectFlight(flight.id)}>
        {content}
      </button>
    );
  }
  return (
    <div className={className} title={flightLabel}>
      {content}
    </div>
  );
}

function FlightDetailDialog({
  save,
  world,
  flight,
  events,
  occurrenceEvents,
  canRedispatch,
  onRedispatchFlight,
  onFetchFlightReleaseDraft,
  onFetchFlightLedgerPreview,
  onClose
}: {
  save: SaveGame;
  world: WorldData;
  flight: FlightInstance;
  events: NonNullable<SaveGame["activeOpsDay"]>["events"];
  occurrenceEvents: FlightOccurrenceEvent[];
  canRedispatch: boolean;
  onRedispatchFlight: RedispatchSubmitHandler;
  onFetchFlightReleaseDraft: FetchFlightReleaseDraftHandler;
  onFetchFlightLedgerPreview: FetchFlightLedgerPreviewHandler;
  onClose: () => void;
}) {
  const [releaseDraftPreview, setReleaseDraftPreview] = useState<FlightReleaseDraft | undefined>();
  const [baseLedgerPreview, setBaseLedgerPreview] = useState<FlightLedgerResult | undefined>();
  const [previewError, setPreviewError] = useState("");
  const effectiveReleaseDraft = flight.releaseDraft ?? releaseDraftPreview;
  const previewFlight = useMemo(() => {
    if (!effectiveReleaseDraft || effectiveReleaseDraft === flight.releaseDraft) {
      return flight;
    }
    return { ...flight, releaseDraft: effectiveReleaseDraft };
  }, [effectiveReleaseDraft, flight]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setReleaseDraftPreview(undefined);
    setBaseLedgerPreview(undefined);
    setPreviewError("");
    if (!canRedispatch) {
      return () => {
        cancelled = true;
      };
    }
    if (!flight.releaseDraft) {
      void onFetchFlightReleaseDraft(flight.id)
        .then((draft) => {
          if (!cancelled) setReleaseDraftPreview(draft);
        })
        .catch((error) => {
          if (!cancelled) {
            setReleaseDraftPreview(undefined);
            setPreviewError(`放行草案生成失败：${errorMessage(error)}`);
          }
        });
    }
    void onFetchFlightLedgerPreview(flight.id)
      .then((ledger) => {
        if (!cancelled) {
          setBaseLedgerPreview(ledger);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setBaseLedgerPreview(undefined);
          setPreviewError(`账单预览失败：${errorMessage(error)}`);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [canRedispatch, flight.id, flight.releaseDraft, onFetchFlightLedgerPreview, onFetchFlightReleaseDraft]);

  return (
    <div
      className="flight-detail-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section className="flight-detail-modal" role="dialog" aria-modal="true" aria-labelledby="flight-detail-title">
        <header className="flight-detail-header">
          <div>
            <strong id="flight-detail-title">{flight.flightNo}</strong>
            <span>
              {flight.origin}-{flight.destination}
              {technicalStopSuffix(flight.technicalStop)} / {formatFlightInstanceStatus(flight.status)}
            </span>
          </div>
          <button className="icon-button" title="关闭" onClick={onClose}>
            <X size={15} />
          </button>
        </header>
        <div className={flightDetailBodyClassName(canRedispatch)} key={flight.id}>
          <FlightInstanceProfile save={save} flight={previewFlight} ledgerPreview={baseLedgerPreview} />
          <FlightEventStream events={events} />
          {canRedispatch && (
            <div className="flight-action-stack">
              {previewError && <div className="inline-error" role="alert">{previewError}</div>}
              <DisruptionDeskPanel world={world} flight={previewFlight} occurrenceEvents={occurrenceEvents} onRedispatchFlight={onRedispatchFlight} />
              <RedispatchPanel
                save={save}
                world={world}
                flight={previewFlight}
                ledgerPreview={baseLedgerPreview}
                onFetchFlightLedgerPreview={onFetchFlightLedgerPreview}
                onRedispatchFlight={onRedispatchFlight}
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function FlightInstanceProfile({ save, flight, ledgerPreview }: { save: SaveGame; flight: FlightInstance; ledgerPreview?: FlightLedgerResult }) {
  const aircraft = save.fleet.find((item) => item.id === flight.aircraftId);
  const plannedRouteChanged = flight.origin !== flight.plannedOrigin || flight.destination !== flight.plannedDestination;
  const groundOps = currentGroundOpsForFlight(flight);
  return (
    <div className="ops-flight-profile">
      <strong>单班追踪</strong>
      <dl>
        <div>
          <dt>航班</dt>
          <dd>{flight.flightNo} / {formatFlightInstanceStatus(flight.status)}</dd>
        </div>
        <div>
          <dt>航线</dt>
          <dd>
            {flight.origin}-{flight.destination}
            {technicalStopSuffix(flight.technicalStop)}
            {plannedRouteSuffix(plannedRouteChanged, flight)}
          </dd>
        </div>
        <div>
          <dt>计划</dt>
          <dd>{dayMinuteToLabel(flight.plannedDepartureMinute)} - {dayMinuteToLabel(flight.plannedArrivalMinute)}</dd>
        </div>
        <div>
          <dt>预计</dt>
          <dd>{dayMinuteToLabel(flight.estimatedDepartureMinute)} - {dayMinuteToLabel(flight.estimatedArrivalMinute)} / D+{Math.round(flight.delayMinutes)}m</dd>
        </div>
        {groundOps && (
          <div>
            <dt>地服</dt>
            <dd>
              {formatGroundServiceStatus(groundOps.status)} / 窗口 {dayMinuteToLabel(groundOps.startWindowMinute)}
              {formatOptionalMinute("开始", groundOps.startedMinute)}
              {formatOptionalMinute("完成", groundOps.readyAtMinute)}
            </dd>
          </div>
        )}
        {flight.readiness && (
          <div>
            <dt>准备</dt>
            <dd>
              最早 {dayMinuteToLabel(flight.readiness.readyAtMinute)} / 过站 {flight.readiness.actualTurnMinutes}m / 响应 {flight.readiness.responseMinutes}m
              {passengerTransferSuffix(flight.readiness.passengerTransferMinutes)}
            </dd>
          </div>
        )}
        <div>
          <dt>实际</dt>
          <dd>{formatActualMinute(flight.actualDepartureMinute)} - {formatActualMinute(flight.actualArrivalMinute)}</dd>
        </div>
        <div>
          <dt>飞机</dt>
          <dd>{aircraftAssignmentLabel(aircraft, flight)} / {formatAssignmentSource(flight.aircraftAssignmentSource)}</dd>
        </div>
        <div>
          <dt>航班串</dt>
          <dd>{flightRotationLabel(save, flight)}</dd>
        </div>
        <div>
          <dt>结果</dt>
          <dd>{flight.passengers} pax / {formatMoney(flight.revenueUsd - flight.costUsd)}</dd>
        </div>
        <div>
          <dt>原因</dt>
          <dd>{flightReasonLabel(flight)}</dd>
        </div>
      </dl>
      <MovementStackPanel flight={flight} />
      <FlightLedgerPanel flight={flight} aircraft={aircraft} previewLedger={ledgerPreview} />
      <GroundOpsNodePanel flight={flight} />
      {flight.releaseDraft && <ReleaseDraftPanel flight={flight} />}
    </div>
  );
}

function technicalStopSuffix(technicalStop: string | undefined): string {
  if (technicalStop) {
    return ` via ${technicalStop}`;
  }
  return "";
}

function flightDetailBodyClassName(canRedispatch: boolean): string {
  if (canRedispatch) {
    return "ops-detail-grid flight-detail-body";
  }
  return "ops-detail-grid flight-detail-body no-actions";
}

function plannedRouteSuffix(plannedRouteChanged: boolean, flight: FlightInstance): string {
  if (plannedRouteChanged) {
    return ` / 原计划 ${flight.plannedOrigin}-${flight.plannedDestination}`;
  }
  return "";
}

function passengerTransferSuffix(passengerTransferMinutes: number | undefined): string {
  if (passengerTransferMinutes) {
    return ` / 转运 ${passengerTransferMinutes}m`;
  }
  return "";
}

function aircraftAssignmentLabel(aircraft: SaveGame["fleet"][number] | undefined, flight: FlightInstance): string {
  if (aircraft) {
    return `${aircraft.registration} / ${aircraft.location}`;
  }
  return flight.aircraftId ?? "未派机";
}

function MovementStackPanel({ flight }: { flight: FlightInstance }) {
  const movements = flight.movements ?? [];
  if (movements.length === 0) return null;
  return (
    <section className="movement-stack-card">
      <header>
        <span>实际运行段</span>
        <em>{movementStackSummary(flight.completionMode, movements.length)}</em>
      </header>
      <div className="movement-stack-list">
        {movements.map((movement) => (
          <div key={movement.id} className={`movement-row ${movement.status}`}>
            <strong>{movement.sequence}. {formatMovementKind(movement.kind)}</strong>
            <span>{movement.origin}-{movement.destination}</span>
            <em>
              {dayMinuteToLabel(movement.actualDepartureMinute ?? movement.estimatedDepartureMinute)} - {dayMinuteToLabel(movement.actualArrivalMinute ?? movement.estimatedArrivalMinute)}
              {movementGroundOpsSuffix(movement.groundOps)}
            </em>
          </div>
        ))}
      </div>
    </section>
  );
}

function movementStackSummary(completionMode: FlightInstance["completionMode"], movementCount: number): string {
  if (completionMode) {
    return formatCompletionMode(completionMode);
  }
  return `${movementCount} 段`;
}

function movementGroundOpsSuffix(groundOps: FlightInstance["movements"][number]["groundOps"]): string {
  if (groundOps) {
    return ` / 地服${formatGroundServiceStatus(groundOps.status)}`;
  }
  return "";
}

function GroundOpsNodePanel({ flight }: { flight: FlightInstance }) {
  const nodes = [...currentGroundOpsNodesForFlight(flight)].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0) || (a.startedMinute ?? a.plannedStartMinute ?? 0) - (b.startedMinute ?? b.plannedStartMinute ?? 0));
  if (nodes.length === 0) {
    return null;
  }
  return (
    <section className="ground-ops-card">
      <header>
        <span>运行节点</span>
        <em>{nodes.filter((node) => node.status === "planned" || node.status === "running" || node.status === "blocked").length} 待完成</em>
      </header>
      <div className="ground-ops-list">
        {nodes.map((node) => {
          const nodeDetail = formatGroundOpsNodeDetail(node, nodes);
          const nodeStatus = formatGroundOpsStatus(node, nodes);
          const nodeMinute = node.completedMinute ?? node.readyAtMinute ?? node.plannedStartMinute ?? node.createdMinute;
          return (
            <div key={node.id} className={`ground-ops-node ${node.status}`}>
              <strong>{formatGroundOpsKind(node.kind)}</strong>
              <span>{nodeDetail}</span>
              <em>{nodeStatus} / {dayMinuteToLabel(nodeMinute)}</em>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ReleaseDraftPanel({ flight }: { flight: FlightInstance }) {
  const draft = flight.releaseDraft;
  if (!draft) return null;
  const visibleCandidates = draft.aircraftCandidates.slice(0, 5);
  return (
    <section className="release-draft-card">
      <header>
        <span>放行草案</span>
        <em>R{draft.revision} / {dayMinuteToLabel(draft.generatedMinute)}</em>
      </header>
      <div className="release-candidate-list">
        {visibleCandidates.map((candidate) => (
          <div key={candidate.aircraftId} className={`release-candidate ${candidate.availability}`}>
            <strong>{candidate.registration}</strong>
            <span>{candidate.typeId.toUpperCase()} / {formatAircraftCandidateAvailability(candidate.availability)}</span>
            <em>{candidate.reason}</em>
          </div>
        ))}
      </div>
      {draft.blockers.length > 0 && (
        <ul className="release-blocker-list">
          {draft.blockers.slice(0, 3).map((blocker) => (
            <li key={blocker}>{blocker}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FlightLedgerPanel({
  flight,
  aircraft,
  previewLedger
}: {
  flight: FlightInstance;
  aircraft: SaveGame["fleet"][number] | undefined;
  previewLedger?: FlightLedgerResult;
}) {
  const payload = flight.payloadPlan ?? previewLedger?.payloadPlan;
  const fuel = flight.fuelPlan ?? previewLedger?.fuelPlan;
  const weight = flight.weightPlan ?? previewLedger?.weightPlan;
  const revenue = flight.revenueBreakdown ?? previewLedger?.revenueBreakdown;
  const cost = flight.costBreakdown ?? previewLedger?.costBreakdown;
  const projectedProfit = (revenue?.totalRevenueUsd ?? flight.revenueUsd) - (cost?.totalCostUsd ?? flight.costUsd);
  const previewSuffix = ledgerPreviewSuffix(flight, previewLedger);
  const delaySummary = flight.delaySummary ?? {
    departureDelayMinutes: Math.round(flight.delayMinutes),
    arrivalDelayMinutes: Math.round(flight.delayMinutes),
    controllableDelayMinutes: 0,
    uncontrollableDelayMinutes: Math.round(flight.delayMinutes)
  };
  const delayEvents = flight.delayEvents ?? [];
  return (
    <div className="flight-ledger-grid">
      <section className="ledger-card">
        <header>
          <span>业载</span>
          <em>{payloadHeaderLabel(payload, previewSuffix)}</em>
        </header>
        <LedgerRows rows={payloadLedgerRows(payload, flight.payloadAdjustment)} />
      </section>
      <section className="ledger-card">
        <header>
          <span>燃油</span>
          <em>{fuelHeaderLabel(fuel, previewSuffix, aircraft)}</em>
        </header>
        <LedgerRows rows={fuelLedgerRows(fuel, aircraft)} />
      </section>
      <section className="ledger-card">
        <header>
          <span>重量</span>
          <em>{weightHeaderLabel(weight)}</em>
        </header>
        {weight && (
          <div className="ledger-meter-stack">
            <LedgerMeter label="ZFW" value={weight.zeroFuelWeightKg} max={weight.maxZeroFuelWeightKg} />
            <LedgerMeter label="TOW" value={weight.takeoffWeightKg} max={weight.maxTakeoffWeightKg} />
            <LedgerMeter label="LDW" value={weight.landingWeightKg} max={weight.maxLandingWeightKg} />
          </div>
        )}
        {!weight && <div className="ledger-empty">等待放行计算</div>}
      </section>
      <section className="ledger-card">
        <header>
          <span>收益/成本</span>
          <em>{formatMoney(projectedProfit)}{previewSuffix}</em>
        </header>
        <LedgerRows rows={financeLedgerRows(revenue, cost)} />
      </section>
      <section className="ledger-card wide">
        <header>
          <span>延误归因</span>
          <em>A+{delaySummary.arrivalDelayMinutes} / 可控 {delaySummary.controllableDelayMinutes}m</em>
        </header>
        <div className="delay-ledger-list">
          {delayEvents.map((event) => (
            <div key={event.id}>
              <span>{formatDelayCode(event.code)}</span>
              <strong>{event.minutes}m</strong>
              <em>{event.message}</em>
            </div>
          ))}
          {delayEvents.length === 0 && <div className="ledger-empty">暂无延误归因</div>}
        </div>
      </section>
    </div>
  );
}

type PayloadPlan = NonNullable<FlightInstance["payloadPlan"]>;
type FuelPlan = NonNullable<FlightInstance["fuelPlan"]>;
type WeightPlan = NonNullable<FlightInstance["weightPlan"]>;
type RevenueBreakdown = NonNullable<FlightInstance["revenueBreakdown"]>;
type CostBreakdown = NonNullable<FlightInstance["costBreakdown"]>;

function ledgerPreviewSuffix(flight: FlightInstance, previewLedger: FlightLedgerResult | undefined): string {
  if (!flight.fuelPlan && previewLedger) {
    return " / 预估";
  }
  return "";
}

function payloadHeaderLabel(payload: PayloadPlan | undefined, previewSuffix: string): string {
  if (payload) {
    return `${payload.passengers} pax / ${formatKg(payload.cargoKg)}${previewSuffix}`;
  }
  return "未放行";
}

function payloadLedgerRows(payload: PayloadPlan | undefined, payloadAdjustment: FlightInstance["payloadAdjustment"]): Array<[string, string]> {
  return [
    ["旅客重量", payloadWeightLabel(payload, "passengerWeightKg")],
    ["行李", payloadWeightLabel(payload, "baggageKg")],
    ["货物", payloadWeightLabel(payload, "cargoKg")],
    ["总业载", payloadWeightLabel(payload, "totalPayloadKg")],
    ["减载", deniedPayloadLabel(payload)],
    ["指令", payloadAdjustmentLabel(payloadAdjustment)]
  ];
}

function payloadWeightLabel(payload: PayloadPlan | undefined, field: "passengerWeightKg" | "baggageKg" | "cargoKg" | "totalPayloadKg"): string {
  if (payload) {
    return formatKg(payload[field]);
  }
  return "--";
}

function deniedPayloadLabel(payload: PayloadPlan | undefined): string {
  if (payload) {
    return `${payload.deniedPassengers} pax / ${formatKg(payload.offloadedCargoKg)}`;
  }
  return "--";
}

function payloadAdjustmentLabel(payloadAdjustment: FlightInstance["payloadAdjustment"]): string {
  if (payloadAdjustment) {
    return `${payloadAdjustment.passengerReduction} pax / ${formatKg(payloadAdjustment.cargoReductionKg)}`;
  }
  return "--";
}

function fuelHeaderLabel(fuel: FuelPlan | undefined, previewSuffix: string, aircraft: SaveGame["fleet"][number] | undefined): string {
  if (fuel) {
    return `放行 ${formatKg(fuel.plannedTakeoffFuelKg)}${previewSuffix}`;
  }
  return aircraftFuelOnBoardLabel(aircraft);
}

function fuelLedgerRows(fuel: FuelPlan | undefined, aircraft: SaveGame["fleet"][number] | undefined): Array<[string, string]> {
  return [
    ["起始剩油", fuelOnBoardBeforeLabel(fuel, aircraft)],
    ["本站加油", fuelUpliftLabel(fuel)],
    ["最低/建议", minimumRecommendedFuelLabel(fuel)],
    ["放行上限", fuelWeightLabel(fuel, "maximumReleaseFuelKg")],
    ["滑行/爬升", taxiClimbFuelLabel(fuel)],
    ["巡航/下降", cruiseDescentFuelLabel(fuel)],
    ["业载/等待", payloadHoldingFuelLabel(fuel)],
    ["备份/备降/最后", reserveFuelLabel(fuel)],
    ["落地剩油", fuelWeightLabel(fuel, "plannedLandingFuelKg")],
    ["本站油价", originFuelPriceLabel(fuel)]
  ];
}

function fuelOnBoardBeforeLabel(fuel: FuelPlan | undefined, aircraft: SaveGame["fleet"][number] | undefined): string {
  if (fuel) {
    return formatKg(fuel.fuelOnBoardBeforeKg);
  }
  return aircraftFuelOnBoardLabel(aircraft, false);
}

function fuelUpliftLabel(fuel: FuelPlan | undefined): string {
  if (!fuel) {
    return "--";
  }
  return `${formatKg(fuel.upliftKg)}${requestedFuelSuffix(fuel.requestedUpliftKg)}`;
}

function requestedFuelSuffix(requestedUpliftKg: number | undefined): string {
  if (requestedUpliftKg !== undefined) {
    return " / 指令";
  }
  return "";
}

function minimumRecommendedFuelLabel(fuel: FuelPlan | undefined): string {
  if (fuel) {
    return `${formatKg(fuel.minimumReleaseFuelKg)} / ${formatKg(fuel.recommendedReleaseFuelKg)}`;
  }
  return "--";
}

function fuelWeightLabel(fuel: FuelPlan | undefined, field: "maximumReleaseFuelKg" | "plannedLandingFuelKg"): string {
  if (fuel) {
    return formatKg(fuel[field]);
  }
  return "--";
}

function taxiClimbFuelLabel(fuel: FuelPlan | undefined): string {
  if (fuel) {
    return `${formatKg(fuel.taxiFuelKg)} / ${formatKg(fuel.climbFuelKg)}`;
  }
  return "--";
}

function cruiseDescentFuelLabel(fuel: FuelPlan | undefined): string {
  if (fuel) {
    return `${formatKg(fuel.cruiseFuelKg)} / ${formatKg(fuel.descentFuelKg)}`;
  }
  return "--";
}

function payloadHoldingFuelLabel(fuel: FuelPlan | undefined): string {
  if (fuel) {
    return `${formatKg(fuel.payloadFuelKg)} / ${formatKg(fuel.holdingFuelKg)}`;
  }
  return "--";
}

function reserveFuelLabel(fuel: FuelPlan | undefined): string {
  if (fuel) {
    return `${formatKg(fuel.contingencyFuelKg)} / ${formatKg(fuel.alternateFuelKg)} / ${formatKg(fuel.finalReserveFuelKg)}`;
  }
  return "--";
}

function originFuelPriceLabel(fuel: FuelPlan | undefined): string {
  if (fuel) {
    return `$${fuel.originFuelPriceUsdPerTon}/t`;
  }
  return "--";
}

function weightHeaderLabel(weight: WeightPlan | undefined): string {
  if (weight) {
    return `${formatKg(weight.takeoffWeightKg)} TOW`;
  }
  return "未计算";
}

function financeLedgerRows(revenue: RevenueBreakdown | undefined, cost: CostBreakdown | undefined): Array<[string, string]> {
  return [
    ["客票", passengerRevenueLabel(revenue)],
    ["货运", cargoRevenueLabel(revenue)],
    ["燃油", fuelCostLabel(cost)],
    ["保障/航路", handlingNavigationCostLabel(cost)],
    ["运行/维修", operationsMaintenanceCostLabel(cost)],
    ["合计成本", totalCostLabel(cost)]
  ];
}

function passengerRevenueLabel(revenue: RevenueBreakdown | undefined): string {
  if (revenue) {
    return formatMoney(revenue.passengerRevenueUsd);
  }
  return "--";
}

function cargoRevenueLabel(revenue: RevenueBreakdown | undefined): string {
  if (revenue) {
    return formatMoney(revenue.cargoRevenueUsd);
  }
  return "--";
}

function fuelCostLabel(cost: CostBreakdown | undefined): string {
  if (cost) {
    return formatMoney(cost.fuelCostUsd);
  }
  return "--";
}

function handlingNavigationCostLabel(cost: CostBreakdown | undefined): string {
  if (cost) {
    return `${formatMoney(cost.handlingCostUsd)} / ${formatMoney(cost.navigationCostUsd)}`;
  }
  return "--";
}

function operationsMaintenanceCostLabel(cost: CostBreakdown | undefined): string {
  if (cost) {
    return `${formatMoney(cost.operationsCostUsd)} / ${formatMoney(cost.maintenanceReserveUsd)}`;
  }
  return "--";
}

function totalCostLabel(cost: CostBreakdown | undefined): string {
  if (cost) {
    return formatMoney(cost.totalCostUsd);
  }
  return "--";
}

function FlightEventStream({ events, compact = false }: { events: NonNullable<SaveGame["activeOpsDay"]>["events"]; compact?: boolean }) {
  return (
    <div className={flightEventStreamClassName(compact)}>
      <strong>
        状态流
        {compact && <span>{events.length} 条</span>}
      </strong>
      <ul className="message-list">
        {events.map((event) => (
          <li key={event.id}>
            <span className="event-minute">{dayMinuteToLabel(event.minute)}</span>
            <span className="event-message" title={event.message}>{event.message}</span>
          </li>
        ))}
        {events.length === 0 && <li>暂无状态流事件。</li>}
      </ul>
    </div>
  );
}

function flightEventStreamClassName(compact: boolean): string {
  if (compact) {
    return "ops-event-stream compact";
  }
  return "ops-event-stream";
}

function formatReputationDelta(value: number): string {
  if (value === 0) {
    return "声誉 0";
  }
  return `声誉 ${value}`;
}

type DisruptionIntent = Extract<RedispatchAction, { type: "resolve_disruption" }>["intent"];

function initialAlternateAirport(flight: FlightInstance, world: WorldData): string {
  if (flight.destination !== flight.plannedDestination) {
    return flight.destination;
  }
  return world.airports.find((airport) => airport.code !== flight.destination)?.code ?? "PEK";
}

function isOccurrenceEventResolved(
  selectedEvent: FlightOccurrenceEvent | undefined,
  decisions: NonNullable<FlightInstance["disruptionDecisions"]>
): boolean {
  if (!selectedEvent) {
    return false;
  }
  return decisions.some((decision) => decision.eventId === selectedEvent.id);
}

function disruptionTargetAirport(intent: DisruptionIntent, alternate: string): string | undefined {
  if (intent === "divert") {
    return alternate;
  }
  return undefined;
}

function disruptionWaitMinutes(intent: DisruptionIntent, waitMinutes: number): number | undefined {
  if (intent === "wait") {
    return waitMinutes;
  }
  return undefined;
}

function pendingActionLabel(pending: boolean, readyLabel: string): string {
  if (pending) {
    return "提交中";
  }
  return readyLabel;
}

function DisruptionDeskPanel({
  world,
  flight,
  occurrenceEvents,
  onRedispatchFlight
}: {
  world: WorldData;
  flight: FlightInstance;
  occurrenceEvents: FlightOccurrenceEvent[];
  onRedispatchFlight: RedispatchSubmitHandler;
}) {
  const { isSubmitting, submit, isPending } = useRedispatchSubmission(onRedispatchFlight);
  const [selectedEventId, setSelectedEventId] = useState(occurrenceEvents[0]?.id ?? "");
  const [alternate, setAlternate] = useState(initialAlternateAirport(flight, world));
  const [waitMinutes, setWaitMinutes] = useState(45);
  const [note, setNote] = useState("AOC 特情处置");
  const selectedEvent = occurrenceEvents.find((event) => event.id === selectedEventId) ?? occurrenceEvents[0];
  const decisions = flight.disruptionDecisions ?? [];
  const unresolvedEvents = occurrenceEvents.filter((event) => !decisions.some((decision) => decision.eventId === event.id));
  const selectedEventResolved = isOccurrenceEventResolved(selectedEvent, decisions);
  const canContinue = flight.status === "interrupted" && flight.destination !== flight.plannedDestination;
  const canResolveSelectedEvent = Boolean(selectedEvent && !selectedEventResolved && flight.status !== "cancelled" && flight.status !== "failed" && flight.status !== "landed");

  useEffect(() => {
    if (!selectedEventId && unresolvedEvents[0]) {
      setSelectedEventId(unresolvedEvents[0].id);
      return;
    }
    if (selectedEventId && unresolvedEvents.length > 0 && !unresolvedEvents.some((event) => event.id === selectedEventId)) {
      setSelectedEventId(unresolvedEvents[0].id);
      return;
    }
    if (selectedEventId && !occurrenceEvents.some((event) => event.id === selectedEventId)) {
      setSelectedEventId(occurrenceEvents[0]?.id ?? "");
    }
  }, [occurrenceEvents, selectedEventId, unresolvedEvents]);

  const resolveAction = (intent: Extract<RedispatchAction, { type: "resolve_disruption" }>["intent"]): RedispatchAction | null => {
    if (!selectedEvent) return null;
    return {
      type: "resolve_disruption",
      flightInstanceId: flight.id,
      eventId: selectedEvent.id,
      intent,
      targetAirport: disruptionTargetAirport(intent, alternate),
      waitMinutes: disruptionWaitMinutes(intent, waitMinutes),
      reason: note
    };
  };
  const submitResolve = (intent: Extract<RedispatchAction, { type: "resolve_disruption" }>["intent"]) => {
    const action = resolveAction(intent);
    if (!canResolveSelectedEvent || !action) return;
    void submit(action);
  };
  const continueAction: RedispatchAction = { type: "continue_to_destination", flightInstanceId: flight.id, reason: note };
  const completeAction: RedispatchAction = { type: "complete_at_current_airport", flightInstanceId: flight.id, reason: note };
  const waitAction = resolveAction("wait");
  const divertAction = resolveAction("divert");
  const continueIntentAction = resolveAction("continue");
  const cancelIntentAction = resolveAction("cancel");

  return (
    <div className="disruption-desk-panel">
      <strong>
        特情处置
        <span>{unresolvedEvents.length} 待决策</span>
      </strong>
      {occurrenceEvents.length === 0 && !canContinue && <div className="desk-empty">当前航班没有 GM 单班特情。</div>}
      {occurrenceEvents.length > 0 && (
        <>
          <label>
            当前特情
            <select value={selectedEvent?.id ?? ""} disabled={isSubmitting} onChange={(event) => setSelectedEventId(event.target.value)}>
              {occurrenceEvents.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title}
                </option>
              ))}
            </select>
          </label>
          {selectedEvent && (
            <section className="disruption-brief">
              <header>
                <span className={`severity ${selectedEvent.severity}`}>{formatSeverity(selectedEvent.severity)}</span>
                <strong>{formatDisruptionPhase(selectedEvent.phase)}</strong>
                <em>{formatDisruptionKind(selectedEvent.kind)}</em>
              </header>
              <p>{selectedEvent.description}</p>
              {selectedEventResolved && <p className="desk-empty">该特情已处置，等待新的事件或后续运行结果。</p>}
              <div className="disruption-risk-row">
                <span>延误 {selectedEvent.riskProfile.delayMinutesMin}-{selectedEvent.riskProfile.delayMinutesMax}m</span>
                <span>备降 {(selectedEvent.riskProfile.diversionRisk * 100).toFixed(0)}%</span>
                <span>舆情 {(selectedEvent.riskProfile.newsRisk * 100).toFixed(0)}%</span>
                <span>取消压力 {(selectedEvent.riskProfile.cancellationPressure * 100).toFixed(0)}%</span>
              </div>
            </section>
          )}
          <label>
            处置记录
            <input value={note} disabled={isSubmitting} onChange={(event) => setNote(event.target.value)} />
          </label>
          <div className="disruption-controls">
            <label>
              等待分钟
              <input type="number" min={5} max={720} value={waitMinutes} disabled={isSubmitting} onChange={(event) => setWaitMinutes(Number(event.target.value))} />
            </label>
            <button disabled={!canResolveSelectedEvent || isSubmitting} aria-busy={Boolean(waitAction && isPending(waitAction))} onClick={() => submitResolve("wait")}>
              {waitAction && isPending(waitAction) && <span className="submit-spinner" aria-hidden="true" />}
              {pendingActionLabel(Boolean(waitAction && isPending(waitAction)), "等待/盘旋")}
            </button>
          </div>
          <div className="disruption-controls">
            <AirportSearch id="disruption-alternate" label="备降机场" world={world} value={alternate} onChange={setAlternate} disabled={isSubmitting} />
            <button disabled={!canResolveSelectedEvent || isSubmitting} aria-busy={Boolean(divertAction && isPending(divertAction))} onClick={() => submitResolve("divert")}>
              {divertAction && isPending(divertAction) && <span className="submit-spinner" aria-hidden="true" />}
              {pendingActionLabel(Boolean(divertAction && isPending(divertAction)), "备降")}
            </button>
          </div>
          <div className="disruption-buttons">
            <button disabled={!canResolveSelectedEvent || isSubmitting} aria-busy={Boolean(continueIntentAction && isPending(continueIntentAction))} onClick={() => submitResolve("continue")}>
              {continueIntentAction && isPending(continueIntentAction) && <span className="submit-spinner" aria-hidden="true" />}
              {pendingActionLabel(Boolean(continueIntentAction && isPending(continueIntentAction)), "继续尝试")}
            </button>
            <button disabled={!canResolveSelectedEvent || isSubmitting} aria-busy={Boolean(cancelIntentAction && isPending(cancelIntentAction))} onClick={() => submitResolve("cancel")}>
              {cancelIntentAction && isPending(cancelIntentAction) && <span className="submit-spinner" aria-hidden="true" />}
              {pendingActionLabel(Boolean(cancelIntentAction && isPending(cancelIntentAction)), "提前取消")}
            </button>
          </div>
        </>
      )}
      {canContinue && (
        <div className="continuation-box">
          <strong>已落地外站 {flight.currentAirport ?? flight.destination}</strong>
          <span>原计划目的地 {flight.plannedDestination}，可继续补飞或手动结束本次商业航班。</span>
          <div className="disruption-buttons">
            <button disabled={isSubmitting} aria-busy={isPending(continueAction)} onClick={() => void submit(continueAction)}>
              {isPending(continueAction) && <span className="submit-spinner" aria-hidden="true" />}
              {pendingActionLabel(isPending(continueAction), "继续飞往原目的地")}
            </button>
            <button disabled={isSubmitting} aria-busy={isPending(completeAction)} onClick={() => void submit(completeAction)}>
              {isPending(completeAction) && <span className="submit-spinner" aria-hidden="true" />}
              {pendingActionLabel(isPending(completeAction), "当前机场结束")}
            </button>
          </div>
        </div>
      )}
      {decisions.length > 0 && (
        <div className="decision-ledger">
          {decisions.slice(-4).reverse().map((decision) => (
            <div key={decision.id}>
              <span>{formatDisruptionIntent(decision.intent)} / {dayMinuteToLabel(decision.minute)}</span>
              <strong>{decision.outcome.summary}</strong>
              <em>{formatReputationDelta(decision.outcome.reputationDelta)} / {formatMoney(decision.outcome.cashDeltaUsd)}</em>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RedispatchPanel({
  save,
  world,
  flight,
  ledgerPreview,
  onFetchFlightLedgerPreview,
  onRedispatchFlight
}: {
  save: SaveGame;
  world: WorldData;
  flight: FlightInstance;
  ledgerPreview?: FlightLedgerResult;
  onFetchFlightLedgerPreview: FetchFlightLedgerPreviewHandler;
  onRedispatchFlight: RedispatchSubmitHandler;
}) {
  const { pendingSubmitKey, isSubmitting, submit } = useRedispatchSubmission(onRedispatchFlight);
  const [delayMinutes, setDelayMinutes] = useState(30);
  const [reason, setReason] = useState("玩家重签派");
  const [aircraftId, setAircraftId] = useState(flight.aircraftId ?? save.fleet[0]?.id ?? "");
  const [destination, setDestination] = useState(flight.destination);
  const [stop, setStop] = useState("DXB");
  const groundOps = currentGroundOpsForFlight(flight);
  const pendingPayloadNode = [...currentGroundOpsNodesForFlight(flight)].reverse().find((node) => (node.status === "planned" || node.status === "running" || node.status === "blocked") && node.kind === "payload_offload");
  const pendingFuelNode = [...currentGroundOpsNodesForFlight(flight)].reverse().find((node) => (node.status === "planned" || node.status === "running" || node.status === "blocked") && (node.kind === "fuel_uplift" || node.kind === "fuel_defuel"));
  const [passengerReduction, setPassengerReduction] = useState(pendingPayloadNode?.requestedPassengerReduction ?? flight.payloadAdjustment?.passengerReduction ?? flight.payloadPlan?.deniedPassengers ?? 0);
  const [cargoReductionKg, setCargoReductionKg] = useState(pendingPayloadNode?.requestedCargoReductionKg ?? flight.payloadAdjustment?.cargoReductionKg ?? flight.payloadPlan?.offloadedCargoKg ?? 0);
  const defaultFuelUpliftKg = pendingFuelNode?.targetFuelUpliftKg ?? flight.fuelOrder?.upliftKg ?? flight.fuelPlan?.upliftKg ?? flight.fuelPlan?.recommendedUpliftKg ?? ledgerPreview?.fuelPlan.recommendedUpliftKg ?? 0;
  const [fuelUpliftKg, setFuelUpliftKg] = useState(defaultFuelUpliftKg);
  const [editableLedgerPreview, setEditableLedgerPreview] = useState<FlightLedgerResult | undefined>(ledgerPreview);
  const [ledgerPreviewError, setLedgerPreviewError] = useState("");
  const aircraftCandidates = flight.releaseDraft?.aircraftCandidates ?? [];
  const aircraftOptions = aircraftCandidates.map((candidate) => ({
    value: candidate.aircraftId,
    label: `${candidate.registration} / ${candidate.typeId.toUpperCase()} / ${formatAircraftCandidateAvailability(candidate.availability)}`,
    detail: `${candidate.location} / ${candidateReadyMinuteLabel(candidate.readyAtMinute)} / ${candidate.reason}`
  }));
  const terminal = ["landed", "cancelled", "failed"].includes(flight.status);
  const departed = flight.status === "airborne" || flight.status === "diverted";
  const actionWindowOpenMinute = flightActionWindowOpenMinute(flight);
  const groundServiceOpenMinute = groundServiceWindowOpenMinute(flight);
  const beforeActionWindow = Boolean(save.activeOpsDay && save.activeOpsDay.currentMinute < groundServiceOpenMinute);
  const beforeDelayWindow = Boolean(save.activeOpsDay && save.activeOpsDay.currentMinute < actionWindowOpenMinute);
  const assignedAircraft = save.fleet.find((aircraft) => aircraft.id === flight.aircraftId);
  const selectedCandidate = aircraftCandidates.find((candidate) => candidate.aircraftId === flight.aircraftId);
  const aircraftReadyMinute = aircraftReleaseReadyMinute(selectedCandidate, groundServiceOpenMinute);
  const aircraftReadyForService = !assignedAircraft || assignedAircraft.location === flight.origin || Boolean(save.activeOpsDay && save.activeOpsDay.currentMinute >= aircraftReadyMinute);
  const releaseConfirmed = flight.status === "released" || flight.releaseDraft?.status === "confirmed" || groundOps?.status === "in_progress" || groundOps?.status === "completed";
  const groundStarted = groundOps?.status === "in_progress" || groundOps?.status === "completed";
  const aircraftSelectionDisabled = isSubmitting || aircraftOptions.length === 0;
  const planEditDisabled = terminal || departed || isSubmitting;
  const actionWindowDisabled = terminal || departed || isSubmitting || beforeDelayWindow;
  const groundStartDisabled = terminal || departed || isSubmitting || beforeActionWindow || !releaseConfirmed || groundStarted || !aircraftReadyForService;
  const actionReadout = releaseActionReadout(flight, beforeActionWindow, groundServiceOpenMinute, aircraftReadyForService, aircraftReadyMinute);
  const releaseActionLabel = formatReleaseActionLabel(flight);
  const defaultAircraftId = flight.aircraftId ?? aircraftOptions[0]?.value ?? "";
  useEffect(() => {
    setFuelUpliftKg(defaultFuelUpliftKg);
  }, [defaultFuelUpliftKg, flight.id]);
  useEffect(() => {
    setEditableLedgerPreview(ledgerPreview);
  }, [flight.id, ledgerPreview]);
  useEffect(() => {
    let cancelled = false;
    if (terminal || departed || (!ledgerPreview && !flight.fuelPlan && fuelUpliftKg <= 0)) {
      setEditableLedgerPreview(ledgerPreview);
      setLedgerPreviewError("");
      return () => {
        cancelled = true;
      };
    }
    const overrides: FlightLedgerPreviewOverrides = {
      fuelOrder: { upliftKg: Math.max(0, Math.round(fuelUpliftKg)) },
      payloadAdjustment: {
        passengerReduction: Math.max(0, Math.round(passengerReduction)),
        cargoReductionKg: Math.max(0, Math.round(cargoReductionKg))
      }
    };
    void onFetchFlightLedgerPreview(flight.id, overrides)
      .then((ledger) => {
        if (!cancelled) {
          setEditableLedgerPreview(ledger);
          setLedgerPreviewError("");
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setEditableLedgerPreview(ledgerPreview);
          setLedgerPreviewError(`账单预览更新失败：${errorMessage(error)}`);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cargoReductionKg, departed, flight.fuelPlan, flight.id, fuelUpliftKg, ledgerPreview, onFetchFlightLedgerPreview, passengerReduction, terminal]);
  useEffect(() => {
    setPassengerReduction(pendingPayloadNode?.requestedPassengerReduction ?? flight.payloadAdjustment?.passengerReduction ?? flight.payloadPlan?.deniedPassengers ?? 0);
    setCargoReductionKg(pendingPayloadNode?.requestedCargoReductionKg ?? flight.payloadAdjustment?.cargoReductionKg ?? flight.payloadPlan?.offloadedCargoKg ?? 0);
  }, [flight.id, flight.payloadAdjustment?.passengerReduction, flight.payloadAdjustment?.cargoReductionKg, flight.payloadPlan?.deniedPassengers, flight.payloadPlan?.offloadedCargoKg, pendingPayloadNode?.requestedPassengerReduction, pendingPayloadNode?.requestedCargoReductionKg]);
  useEffect(() => {
    setAircraftId(defaultAircraftId);
  }, [defaultAircraftId, flight.id]);
  return (
    <div className="redispatch-panel">
      <strong>放行 / 重签派</strong>
      {ledgerPreviewError && <div className="inline-error" role="alert">{ledgerPreviewError}</div>}
      <label>
        原因
          <input value={reason} disabled={isSubmitting} onChange={(event) => setReason(event.target.value)} />
        </label>
      <FuelOrderControl
        fuel={editableLedgerPreview?.fuelPlan ?? ledgerPreview?.fuelPlan ?? flight.fuelPlan}
        fuelUpliftKg={fuelUpliftKg}
        setFuelUpliftKg={setFuelUpliftKg}
        disabled={planEditDisabled}
        submitting={pendingSubmitKey === redispatchSubmitKey({ type: "set_fuel", flightInstanceId: flight.id, upliftKg: fuelUpliftKg, reason })}
        onSubmit={() => void submit({ type: "set_fuel", flightInstanceId: flight.id, upliftKg: fuelUpliftKg, reason })}
      />
      <div className="redispatch-row">
        <div className="form-readout">当前 {formatFlightInstanceStatus(flight.status)} / {actionReadout}</div>
        <RedispatchSubmitButton className="primary-command" action={{ type: "confirm_release_plan", flightInstanceId: flight.id, reason }} disabled={terminal || departed || groundStarted || isSubmitting} pendingSubmitKey={pendingSubmitKey} onSubmit={submit}>
          {releaseActionLabel}
        </RedispatchSubmitButton>
        <RedispatchSubmitButton action={{ type: "start_ground_service", flightInstanceId: flight.id, reason }} disabled={groundStartDisabled} pendingSubmitKey={pendingSubmitKey} onSubmit={submit}>
          开保障
        </RedispatchSubmitButton>
        <RedispatchSubmitButton action={{ type: "unbind_rotation", flightInstanceId: flight.id, reason }} disabled={terminal || departed || !flight.rotationId || flight.rotationBinding === "manually_unbound" || isSubmitting} pendingSubmitKey={pendingSubmitKey} onSubmit={submit}>
          解绑本班
        </RedispatchSubmitButton>
      </div>
      <div className="redispatch-row">
        <label>
          延误分钟
          <input type="number" min={1} max={720} value={delayMinutes} disabled={isSubmitting} onChange={(event) => setDelayMinutes(Number(event.target.value))} />
        </label>
        <RedispatchSubmitButton action={{ type: "delay", flightInstanceId: flight.id, delayMinutes, reason }} disabled={actionWindowDisabled} pendingSubmitKey={pendingSubmitKey} onSubmit={submit}>
          延误
        </RedispatchSubmitButton>
        <RedispatchSubmitButton action={{ type: "cancel", flightInstanceId: flight.id, reason }} disabled={terminal || departed || isSubmitting} pendingSubmitKey={pendingSubmitKey} onSubmit={submit}>
          取消航班
        </RedispatchSubmitButton>
      </div>
      <div className="redispatch-row">
        <label>
          替换飞机
          <SearchableChoice
            id="redispatch-aircraft"
            value={aircraftId}
            options={aircraftOptions}
            onChange={setAircraftId}
            disabled={aircraftSelectionDisabled}
            placeholder="机号 / 机型 / 位置"
          />
        </label>
        <RedispatchSubmitButton action={{ type: "swap_aircraft", flightInstanceId: flight.id, aircraftId, reason }} disabled={terminal || departed || aircraftSelectionDisabled || !aircraftId} pendingSubmitKey={pendingSubmitKey} onSubmit={submit}>
          换飞机
        </RedispatchSubmitButton>
      </div>
      <div className="redispatch-row">
        <AirportSearch id="redispatch-destination" label="新目的地" world={world} value={destination} onChange={setDestination} disabled={isSubmitting} />
        <RedispatchSubmitButton action={{ type: "reroute", flightInstanceId: flight.id, destination, reason }} disabled={terminal || isSubmitting} pendingSubmitKey={pendingSubmitKey} onSubmit={submit}>
          改航
        </RedispatchSubmitButton>
        <RedispatchSubmitButton action={{ type: "divert", flightInstanceId: flight.id, airportCode: destination, reason }} disabled={terminal || isSubmitting} pendingSubmitKey={pendingSubmitKey} onSubmit={submit}>
          备降
        </RedispatchSubmitButton>
      </div>
      <div className="redispatch-row">
        <AirportSearch id="redispatch-stop" label="技术经停" world={world} value={stop} onChange={setStop} disabled={isSubmitting} />
        <RedispatchSubmitButton action={{ type: "technical_stop", flightInstanceId: flight.id, airportCode: stop, reason }} disabled={terminal || departed || isSubmitting} pendingSubmitKey={pendingSubmitKey} onSubmit={submit}>
          加经停
        </RedispatchSubmitButton>
      </div>
      <div className="redispatch-row payload-adjust-row">
        <label>
          减载旅客
          <input type="number" min={0} max={999} value={passengerReduction} disabled={isSubmitting} onChange={(event) => setPassengerReduction(Number(event.target.value))} />
        </label>
        <label>
          减载货物 kg
          <input type="number" min={0} step={100} value={cargoReductionKg} disabled={isSubmitting} onChange={(event) => setCargoReductionKg(Number(event.target.value))} />
        </label>
        <RedispatchSubmitButton action={{ type: "adjust_payload", flightInstanceId: flight.id, passengerReduction, cargoReductionKg, reason }} disabled={planEditDisabled} pendingSubmitKey={pendingSubmitKey} onSubmit={submit}>
          保存业载
        </RedispatchSubmitButton>
      </div>
    </div>
  );
}

function candidateReadyMinuteLabel(readyAtMinute: number): string {
  if (readyAtMinute === Number.MAX_SAFE_INTEGER) {
    return "--";
  }
  return dayMinuteToLabel(readyAtMinute);
}

function useRedispatchSubmission(onRedispatchFlight: RedispatchSubmitHandler) {
  const [pendingSubmitKey, setPendingSubmitKey] = useState("");
  const pendingRef = useRef("");

  const submit = async (action: RedispatchAction) => {
    if (pendingRef.current) return;
    const actionKey = redispatchSubmitKey(action);
    pendingRef.current = actionKey;
    setPendingSubmitKey(actionKey);
    try {
      await onRedispatchFlight(action);
    } finally {
      pendingRef.current = "";
      setPendingSubmitKey("");
    }
  };

  return {
    pendingSubmitKey,
    isSubmitting: Boolean(pendingSubmitKey),
    submit,
    isPending: (action: RedispatchAction) => pendingSubmitKey === redispatchSubmitKey(action)
  };
}

function RedispatchSubmitButton({
  action,
  children,
  className,
  disabled,
  pendingSubmitKey,
  onSubmit
}: {
  action: RedispatchAction;
  children: string;
  className?: string;
  disabled?: boolean;
  pendingSubmitKey: string;
  onSubmit: (action: RedispatchAction) => Promise<void>;
}) {
  const pending = pendingSubmitKey === redispatchSubmitKey(action);
  return (
    <button className={className} disabled={disabled} aria-busy={pending} onClick={() => void onSubmit(action)}>
      {pending && <span className="submit-spinner" aria-hidden="true" />}
      {pendingActionLabel(pending, children)}
    </button>
  );
}

function redispatchSubmitKey(action: RedispatchAction): string {
  if (action.type === "resolve_disruption") {
    return `${action.flightInstanceId}:${action.type}:${action.eventId}:${action.intent}`;
  }
  return `${action.flightInstanceId}:${action.type}`;
}

function FuelOrderControl({
  fuel,
  fuelUpliftKg,
  setFuelUpliftKg,
  disabled,
  submitting,
  onSubmit
}: {
  fuel: FlightInstance["fuelPlan"];
  fuelUpliftKg: number;
  setFuelUpliftKg: (value: number) => void;
  disabled: boolean;
  submitting: boolean;
  onSubmit: () => void;
}) {
  const sliderMax = Math.max(0, fuel?.maximumUpliftKg ?? fuelUpliftKg, fuelUpliftKg);
  const releaseFuelKg = releaseFuelForOrder(fuel, fuelUpliftKg);
  const minimumUpliftKg = minimumFuelUplift(fuel);
  const sliderMin = Math.min(minimumUpliftKg, sliderMax || 1);
  const lowerThanMinimum = fuel && releaseFuelKg !== undefined && releaseFuelKg < fuel.minimumReleaseFuelKg;
  const aboveMaximum = fuel && releaseFuelKg !== undefined && releaseFuelKg > fuel.maximumReleaseFuelKg;
  const invalidFuelOrder = Boolean(lowerThanMinimum || aboveMaximum);
  const status = fuelOrderStatus(fuel, releaseFuelKg, lowerThanMinimum, aboveMaximum);

  return (
    <div className="fuel-order-panel">
      <header>
        <span>放行油</span>
        <em>{status}</em>
      </header>
      <div className="fuel-order-readouts">
        <div>
          <span>当前</span>
          <strong>{fuelOrderWeightLabel(fuel, "fuelOnBoardBeforeKg")}</strong>
        </div>
        <div>
          <span>最低</span>
          <strong>{fuelOrderWeightLabel(fuel, "minimumReleaseFuelKg")}</strong>
        </div>
        <div>
          <span>建议</span>
          <strong>{fuelOrderWeightLabel(fuel, "recommendedReleaseFuelKg")}</strong>
        </div>
        <div>
          <span>上限</span>
          <strong>{fuelOrderWeightLabel(fuel, "maximumReleaseFuelKg")}</strong>
        </div>
      </div>
      <label className="fuel-order-input">
        本站加油 kg
        <input
          type="number"
          min={minimumUpliftKg}
          step={500}
          value={fuelUpliftKg}
          aria-invalid={invalidFuelOrder}
          disabled={disabled}
          onChange={(event) => setFuelUpliftKg(Math.max(0, Number(event.target.value)))}
        />
      </label>
      <input
        className="fuel-order-slider"
        type="range"
        min={sliderMin}
        max={sliderMax || 1}
        step={500}
        value={fuelOrderSliderValue(sliderMin, sliderMax, fuelUpliftKg)}
        disabled={disabled}
        onChange={(event) => setFuelUpliftKg(Number(event.target.value))}
        aria-label="本站加油量"
      />
      <div className="fuel-order-footer">
        <span>放行 {releaseFuelLabel(releaseFuelKg)} / 加油 {formatKg(fuelUpliftKg)}</span>
        <button disabled={disabled || !fuel || invalidFuelOrder} aria-busy={submitting} onClick={onSubmit}>
          {fuelOrderSubmitIcon(submitting)}
          {fuelOrderSubmitLabel(submitting)}
        </button>
      </div>
    </div>
  );
}

function releaseFuelForOrder(fuel: FlightInstance["fuelPlan"], fuelUpliftKg: number): number | undefined {
  if (!fuel) {
    return undefined;
  }
  const nonNegativeUpliftKg = Math.max(0, fuelUpliftKg);
  return fuel.fuelOnBoardBeforeKg + nonNegativeUpliftKg;
}

function fuelOrderSliderValue(sliderMin: number, sliderMax: number, fuelUpliftKg: number): number {
  const protectedSliderMax = sliderMax || 1;
  const protectedValue = Math.max(sliderMin, fuelUpliftKg);
  return Math.min(protectedValue, protectedSliderMax);
}

function minimumFuelUplift(fuel: FlightInstance["fuelPlan"]): number {
  if (!fuel) {
    return 0;
  }
  const shortfallKg = fuel.minimumReleaseFuelKg - fuel.fuelOnBoardBeforeKg;
  const roundedShortfallKg = Math.ceil(shortfallKg);
  return Math.max(0, roundedShortfallKg);
}

function fuelOrderWeightLabel(
  fuel: FlightInstance["fuelPlan"],
  field: "fuelOnBoardBeforeKg" | "minimumReleaseFuelKg" | "recommendedReleaseFuelKg" | "maximumReleaseFuelKg"
): string {
  if (fuel) {
    return formatKg(fuel[field]);
  }
  return "--";
}

function releaseFuelLabel(releaseFuelKg: number | undefined): string {
  if (releaseFuelKg === undefined) {
    return "--";
  }
  return formatKg(releaseFuelKg);
}

function fuelOrderSubmitIcon(submitting: boolean): ReactNode {
  if (submitting) {
    return <span className="submit-spinner" aria-hidden="true"/>;
  }
  return <Fuel size={14}/>;
}

function fuelOrderSubmitLabel(submitting: boolean): string {
  if (submitting) {
    return "保存中";
  }
  return "保存加油计划";
}

function legDelayLabel(leg: SaveGame["operationsLog"][number]["legs"][number]): string {
  const arrivalDelayMinutes = Math.round(leg.arrivalDelayMinutes ?? 0);
  const propagatedDelayMinutes = Math.round(leg.propagatedDelayMinutes ?? 0);
  return `A+${arrivalDelayMinutes} / P+${propagatedDelayMinutes}`;
}

function OperationTimeline({ operations }: { operations: SaveGame["operationsLog"] }) {
  return (
    <div className="operation-timeline">
      {operations.map((operation) => (
        <article key={operation.id} className="rotation-block">
          <header>
            <strong>{operation.title}</strong>
            <span>{formatRotationStatus(operation.status)} / {operation.aircraftId ?? "未派机"}</span>
          </header>
          <div className="leg-strip">
            {operation.legs.map((leg) => (
              <div key={leg.id} className={`leg-chip ${leg.status}`}>
                <strong>{leg.flightNo}</strong>
                <span>{leg.origin}-{leg.destination}</span>
                <em>{leg.departureTimeLocal} -&gt; {leg.arrivalTimeLocal}</em>
                <small>{legDelayLabel(leg)}</small>
              </div>
            ))}
          </div>
        </article>
      ))}
      {operations.length === 0 && <div className="empty-state">尚无运行记录，运行日正常结束后会生成航段时间轴。</div>}
    </div>
  );
}

export function recoverySourceLabel(save: SaveGame, flightInstanceId: string | undefined): string {
  if (!flightInstanceId) {
    return "独立";
  }
  const source = save.activeOpsDay?.flights.find((flight) => flight.id === flightInstanceId);
  return recoverySourceFlightLabel(source, flightInstanceId);
}

function recoverySourceFlightLabel(source: FlightInstance | undefined, fallbackId: string): string {
  if (source) {
    return `${source.flightNo} / ${source.origin}-${source.destination}`;
  }
  return fallbackId;
}

function aircraftTimelineLabel(aircraft: SaveGame["fleet"][number] | undefined): string {
  if (aircraft) {
    return aircraft.registration;
  }
  return "未派机";
}

function aircraftTimelineSubLabel(aircraft: SaveGame["fleet"][number] | undefined): string {
  if (aircraft) {
    return `${aircraft.typeId.toUpperCase()} / ${aircraft.location}`;
  }
  return "等待自动或手动排班";
}

function aircraftTimelineGroups(save: SaveGame, flights: FlightInstance[]) {
  const groups = new Map<string, FlightInstance[]>();
  for (const flight of flights) {
    const key = flight.aircraftId ?? "unassigned";
    groups.set(key, [...(groups.get(key) ?? []), flight]);
  }
  return [...groups.entries()]
    .map(([id, groupFlights]) => {
      const aircraft = save.fleet.find((item) => item.id === id);
      return {
        id,
        label: aircraftTimelineLabel(aircraft),
        subLabel: aircraftTimelineSubLabel(aircraft),
        flights: groupFlights.sort((a, b) => a.estimatedDepartureMinute - b.estimatedDepartureMinute || a.flightNo.localeCompare(b.flightNo))
      };
    })
    .sort((a, b) => {
      if (a.id === "unassigned") return 1;
      if (b.id === "unassigned") return -1;
      return (a.flights[0]?.estimatedDepartureMinute ?? 0) - (b.flights[0]?.estimatedDepartureMinute ?? 0) || a.label.localeCompare(b.label);
    });
}

function timelineTicks(minMinute: number, maxMinute: number): number[] {
  const first = Math.ceil(minMinute / 360) * 360;
  const ticks: number[] = [];
  for (let minute = first; minute <= maxMinute; minute += 360) {
    ticks.push(minute);
  }
  return ticks;
}

function turnWindowForFlight(flight: FlightInstance, previousFlight: FlightInstance | undefined, minMinute: number, maxMinute: number, span: number) {
  if (flight.readiness) {
    const startMinute = flight.readiness.sourceArrivalMinute ?? flight.readiness.generatedMinute;
    const endMinute = Math.max(startMinute, flight.readiness.readyAtMinute);
    return buildTimelineTurnWindow({
      startMinute,
      endMinute,
      minMinute,
      maxMinute,
      span,
      minimumTurnMinutes: flight.readiness.minimumTurnMinutes,
      actualTurnMinutes: flight.readiness.actualTurnMinutes,
      titleParts: [
        `恢复准备 ${dayMinuteToLabel(startMinute)} - ${dayMinuteToLabel(endMinute)}`,
        `最短 ${flight.readiness.minimumTurnMinutes}m`,
        `实际 ${flight.readiness.actualTurnMinutes}m`,
        `响应 ${flight.readiness.responseMinutes}m`,
        ...readinessTransferTitleParts(flight.readiness)
      ],
      label: "准备"
    });
  }
  if (!previousFlight || previousFlight.destination !== flight.origin || isTerminalWithoutArrival(previousFlight)) {
    return undefined;
  }
  const startMinute = previousFlight.actualArrivalMinute ?? previousFlight.estimatedArrivalMinute;
  const endMinute = flight.actualDepartureMinute ?? flight.estimatedDepartureMinute;
  const actualTurnMinutes = endMinute - startMinute;
  if (actualTurnMinutes < 0) return undefined;
  const minimumTurnMinutes = Math.max(0, previousFlight.minimumTurnMinutes);
  return buildTimelineTurnWindow({
    startMinute,
    endMinute,
    minMinute,
    maxMinute,
    span,
    minimumTurnMinutes,
    actualTurnMinutes,
    titleParts: [
      `过站窗口 ${previousFlight.flightNo} -> ${flight.flightNo} ${dayMinuteToLabel(startMinute)} - ${dayMinuteToLabel(endMinute)}`,
      `最短 ${minimumTurnMinutes}m`,
      `实际 ${actualTurnMinutes}m`
    ],
    label: "过站"
  });
}

function buildTimelineTurnWindow(input: {
  startMinute: number;
  endMinute: number;
  minMinute: number;
  maxMinute: number;
  span: number;
  minimumTurnMinutes: number;
  actualTurnMinutes: number;
  titleParts: string[];
  label: string;
}) {
  const { startMinute, endMinute, minMinute, maxMinute, span, minimumTurnMinutes } = input;
  if (endMinute < minMinute || startMinute > maxMinute) return undefined;
  const visibleStart = clamp(startMinute, minMinute, maxMinute);
  const visibleEnd = clamp(endMinute, minMinute, maxMinute);
  const width = Math.max(((visibleEnd - visibleStart) / span) * 100, 0.5);
  const minimumMarkerMinute = startMinute + minimumTurnMinutes;
  const shortfallMinutes = Math.max(0, minimumTurnMinutes - input.actualTurnMinutes);
  const minimumMarkerLeft = timelineMinimumMarkerLeft(startMinute, endMinute, minimumMarkerMinute);
  const titleParts = timelineTurnWindowTitleParts(input.titleParts, shortfallMinutes);
  return {
    style: {
      left: `${((visibleStart - minMinute) / span) * 100}%`,
      width: `${width}%`
    },
    title: titleParts.join(" / "),
    minimumMarkerLeft,
    showLabel: width >= 5,
    label: input.label,
    shortfallMinutes
  };
}

function timelineMinimumMarkerLeft(startMinute: number, endMinute: number, minimumMarkerMinute: number): number | undefined {
  if (endMinute <= startMinute || minimumMarkerMinute <= startMinute) {
    return undefined;
  }
  const elapsedToMinimumMarker = minimumMarkerMinute - startMinute;
  const totalWindowMinutes = endMinute - startMinute;
  const markerPercent = (elapsedToMinimumMarker / totalWindowMinutes) * 100;
  return clamp(markerPercent, 0, 100);
}

function timelineTurnWindowTitleParts(titleParts: string[], shortfallMinutes: number): string[] {
  if (shortfallMinutes > 0) {
    return [...titleParts, `不足 ${shortfallMinutes}m`];
  }
  return titleParts;
}

function previousTurnSource(flights: FlightInstance[], index: number) {
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    const flight = flights[cursor];
    if (!isTerminalWithoutArrival(flight)) {
      return flight;
    }
  }
  return undefined;
}

function isTerminalWithoutArrival(flight: FlightInstance) {
  return flight.status === "cancelled" || flight.status === "failed";
}

function activeAircraftFlight(save: SaveGame, aircraftId: string): FlightInstance | undefined {
  return save.activeOpsDay?.flights.find((flight) => flight.aircraftId === aircraftId && !["landed", "cancelled", "failed"].includes(flight.status));
}

function nextAircraftFlight(flights: FlightInstance[], aircraftId: string): FlightInstance | undefined {
  return flights
    .filter((flight) => flight.aircraftId === aircraftId && !["landed", "cancelled", "failed"].includes(flight.status))
    .sort((a, b) => a.estimatedDepartureMinute - b.estimatedDepartureMinute)[0];
}

function formatDelayCode(code: FlightInstance["delayEvents"][number]["code"]): string {
  if (code === "late_aircraft") return "前序";
  if (code === "airspace") return "空域";
  if (code === "turnaround") return "过站";
  if (code === "fueling") return "加油";
  if (code === "gm_directive") return "GM";
  if (code === "recovery") return "恢复";
  if (code === "player_action") return "玩家";
  return "其他";
}

function formatDisruptionPhase(phase: FlightOccurrenceEvent["phase"]): string {
  if (phase === "pre_departure") return "起飞前";
  if (phase === "enroute") return "空中";
  if (phase === "arrival") return "进近/到达";
  return "地面";
}

function formatDisruptionKind(kind: FlightOccurrenceEvent["kind"]): string {
  if (kind === "weather") return "天气";
  if (kind === "mechanical") return "机务";
  if (kind === "medical_transfer") return "医疗";
  if (kind === "security") return "安保";
  if (kind === "airport_visibility") return "能见度";
  return "运行";
}

function formatDisruptionIntent(intent: FlightInstance["disruptionDecisions"][number]["intent"]): string {
  if (intent === "wait") return "等待/盘旋";
  if (intent === "divert") return "备降";
  if (intent === "cancel") return "取消";
  return "继续";
}

function formatMovementKind(kind: FlightInstance["movements"][number]["kind"]): string {
  if (kind === "diversion") return "备降段";
  if (kind === "continuation") return "补飞段";
  if (kind === "positioning") return "调机/恢复";
  return "计划段";
}

function formatCompletionMode(mode: NonNullable<FlightInstance["completionMode"]>): string {
  if (mode === "arrived_planned_destination") return "抵达原目的地";
  if (mode === "manual_closed") return "手动结束";
  if (mode === "cancelled") return "已取消";
  return "失败";
}

function formatGroundOpsKind(kind: GroundOpsNodeKind): string {
  if (kind === "payload_offload") return "减载";
  if (kind === "fuel_defuel") return "抽油";
  if (kind === "fuel_uplift") return "加油";
  if (kind === "cabin_cleaning") return "清洁";
  if (kind === "water_service") return "上水";
  if (kind === "catering") return "餐食";
  if (kind === "passenger_boarding") return "旅客";
  if (kind === "baggage_loading") return "行李";
  if (kind === "cargo_loading") return "货邮";
  if (kind === "loadsheet") return "舱单";
  if (kind === "doors_closed") return "关门";
  if (kind === "chocks_off") return "撤挡";
  if (kind === "pushback") return "推出";
  return "滑行";
}

export function formatGroundOpsStatus(node: GroundOpsNodeState, nodes: GroundOpsNodeState[] = []): string {
  if (isWaitingForGroundOpsDependency(node, nodes)) {
    return "等待前序完成";
  }
  const status = node.status;
  if (status === "completed") return "完成";
  if (status === "cancelled") return "取消";
  if (status === "blocked") return "阻断";
  if (status === "planned") return "待开始";
  return "执行中";
}

function formatGroundServiceStatus(status: GroundOpsFlowStatus): string {
  if (status === "completed") return "完成";
  if (status === "in_progress") return "执行中";
  return "未开始";
}

export function formatGroundOpsNodeDetail(node: GroundOpsNodeState, nodes: GroundOpsNodeState[] = []): string {
  if (isWaitingForGroundOpsDependency(node, nodes)) {
    return "等待前序完成";
  }
  if (node.kind === "payload_offload") {
    return `${node.requestedPassengerReduction ?? 0} pax / ${formatKg(node.requestedCargoReductionKg ?? 0)}`;
  }
  if (node.kind === "fuel_uplift" || node.kind === "fuel_defuel") {
    const delta = Math.abs(Math.round(node.requestedFuelDeltaKg ?? 0));
    const target = targetFuelUpliftSuffix(node.targetFuelUpliftKg);
    return `${formatKg(delta)}${target}`;
  }
  const start = node.startedMinute ?? node.plannedStartMinute ?? node.createdMinute;
  const end = node.readyAtMinute ?? node.completedMinute ?? start;
  return `${dayMinuteToLabel(start)} - ${dayMinuteToLabel(end)}`;
}

function isWaitingForGroundOpsDependency(node: GroundOpsNodeState, nodes: GroundOpsNodeState[]): boolean {
  if (node.status !== "planned") {
    return false;
  }
  return (node.dependsOnNodeIds ?? []).some((dependencyId) => {
    const dependency = nodes.find((item) => item.id === dependencyId);
    return dependency?.status !== "completed";
  });
}

function targetFuelUpliftSuffix(targetFuelUpliftKg: number | undefined): string {
  if (targetFuelUpliftKg === undefined) {
    return "";
  }
  return ` / 目标加油 ${formatKg(targetFuelUpliftKg)}`;
}

export function formatFlightInstanceStatus(status: FlightInstance["status"]): string {
  if (status === "scheduled") return "计划";
  if (status === "awaiting_release") return "待放行";
  if (status === "needs_recovery") return "待恢复";
  if (status === "released") return "计划已确认";
  if (status === "ground_service") return "地面保障";
  if (status === "ready_for_departure") return "待起飞";
  if (status === "delayed") return "延误";
  if (status === "airborne") return "空中";
  if (status === "diverted") return "备降";
  if (status === "interrupted") return "外站中断";
  if (status === "landed") return "已落地";
  if (status === "cancelled") return "取消";
  return "失败";
}

function formatReleaseActionLabel(flight: FlightInstance): string {
  if (flight.status === "released" || flight.releaseDraft?.status === "confirmed") return "更新计划";
  if (flight.status === "needs_recovery" || flight.reason?.includes("放行未通过") || flight.reason?.includes("重新放行")) return "重新放行";
  return "确认计划";
}

function formatAssignmentSource(source: FlightInstance["aircraftAssignmentSource"]): string {
  if (source === "auto") return "自动派机";
  if (source === "manual") return "手工派机";
  return "待派机";
}

function formatAircraftCandidateAvailability(value: NonNullable<FlightInstance["releaseDraft"]>["aircraftCandidates"][number]["availability"]): string {
  if (value === "available_now") return "本场可用";
  if (value === "arrives_before_departure") return "起飞前到达";
  return "不可用";
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error || "未知错误");
}

function clamp(value: number, min: number, max: number) {
  const protectedValue = Math.max(min, value);
  return Math.min(max, protectedValue);
}
